
import React, { createContext, useContext, useState, ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { User } from '../types';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/hooks/use-toast';
import { getUser, updateUser } from '@/services/userService';
import {
  getCurrentSession,
  resetPasswordForEmail,
  setSession,
  signInWithEmail,
  signOut as supabaseSignOut,
  updateAuthUser,
} from '@/services/authService';
import { COOKIE_KEYS, deleteCookie, setCookie } from '@/utils/cookies';
import { buildUserAvatarPath, getUserAvatarSignedUrl } from '@/services/avatarService';
import {
  checkLoginRateLimit,
  logAuthEvent,
  recordFailedLoginAttempt,
  resetFailedLoginAttempts,
} from '@/services/authAuditService';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateUserProfile: (userData: Partial<User>) => Promise<boolean>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  refreshCurrentUser: () => Promise<User | null>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const GDPR_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const CHANGELOG_LOGIN_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SESSION_PROFILE_REFRESH_INTERVAL_MS = 30 * 1000;
const ACCESS_ALLOWED_STATUSES = new Set([
  'active',
  'activa',
  'pending_age_declaration',
  'pending_deletion',
  'pendiente_baja',
]);

const resolveLoginErrorMessage = (message: string | null): string => {
  const normalizedMessage = message?.trim().toLowerCase() ?? null;

  if (normalizedMessage === 'email not confirmed') {
    return 'Email no confirmado. Por favor, revisa tu bandeja de entrada para confirmar tu cuenta.';
  }

  if (normalizedMessage === 'invalid login credentials') {
    return 'Email o contraseña incorrectos';
  }

  if (normalizedMessage === 'user is banned') {
    return 'Usuario bloqueado';
  }

  return message ?? 'Email o contraseña incorrectos';
};

const omitUndefinedFields = <T extends object>(value: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined)
  ) as Partial<T>;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const profileRefreshInFlightRef = React.useRef(false);
  const currentUserId = currentUser?.id ?? null;
  const { toast } = useToast();

  const resolveErrorMessage = React.useCallback((error: unknown, fallback: string) => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
    }
    return fallback;
  }, []);

  const enhanceUserWithAvatar = React.useCallback(async (user: User | null): Promise<User | null> => {
    if (!user) {
      return null;
    }

    const next: User = { ...user };
    const rawAvatar = typeof next.avatar === 'string' ? next.avatar.trim() : '';

    if (rawAvatar && /^https?:\/\//i.test(rawAvatar)) {
      next.avatar = rawAvatar;
      next.avatarPath = typeof next.avatarPath === 'string' ? next.avatarPath : null;
      return next;
    }

    const currentPath = typeof next.avatarPath === 'string' && next.avatarPath.trim().length > 0
      ? next.avatarPath.trim()
      : undefined;
    const storagePath = currentPath ?? buildUserAvatarPath(next.id);

    try {
      const signedUrl = await getUserAvatarSignedUrl(storagePath);
      if (signedUrl) {
        next.avatar = signedUrl;
        next.avatarPath = storagePath;
      } else {
        next.avatarPath = null;
        delete next.avatar;
      }
    } catch (error) {
      console.warn('No se pudo obtener la imagen de perfil del usuario:', error);
      next.avatarPath = null;
      delete next.avatar;
    }

    return next;
  }, []);

  const markUserHasLoggedIn = React.useCallback(() => {
    setCookie(COOKIE_KEYS.CHANGELOG_HAS_LOGGED_IN, '1', {
      maxAge: CHANGELOG_LOGIN_COOKIE_MAX_AGE,
      sameSite: 'Lax',
    });
  }, []);

  const clearClientSession = React.useCallback(() => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
    deleteCookie(COOKIE_KEYS.SIDEBAR_STATE);
  }, []);

  const syncUserFromSession = React.useCallback(
    async (session: Session | null) => {
      if (!session?.user) {
        clearClientSession();
        return;
      }

      try {
        const profile = await getUser(session.user.id);
        if (profile) {
          const normalizedEmail = profile.email && profile.email.length > 0
            ? profile.email
            : session.user.email ?? '';
          if (!ACCESS_ALLOWED_STATUSES.has(profile.status ?? 'inactive')) {
            clearClientSession();
            await supabaseSignOut();
            return;
          }
          const normalized = { ...profile, email: normalizedEmail };
          const enhanced = await enhanceUserWithAvatar(normalized);
          if (enhanced) {
            setCurrentUser(enhanced);
            localStorage.setItem('currentUser', JSON.stringify(enhanced));
            markUserHasLoggedIn();
          } else {
            clearClientSession();
          }
        } else {
          clearClientSession();
        }
      } catch {
        clearClientSession();
      }
    },
    [clearClientSession, enhanceUserWithAvatar, markUserHasLoggedIn]
  );

  // Login contra la tabla de usuarios
  const login = React.useCallback(async (email: string, password: string): Promise<boolean> => {
    const trimmedEmail = email.trim().toLowerCase();
    const rateLimit = checkLoginRateLimit(trimmedEmail);

    if (rateLimit.isRateLimited) {
      void logAuthEvent({
        action: 'auth.rate_limited',
        severity: 'critical',
        email: trimmedEmail,
        details: {
          reason: 'consecutive_login_failures_threshold_exceeded',
          consecutiveFailures: rateLimit.consecutiveFailures,
          retryAfterSeconds: rateLimit.retryAfterSeconds,
          scope: 'login',
        },
      });
      toast({
        title: 'Demasiados intentos fallidos',
        description: `Has superado el límite de intentos. Por favor, espera ${rateLimit.retryAfterSeconds} segundos antes de volver a intentarlo.`,
        variant: 'destructive',
        duration: 5000,
      });
      return false;
    }

    try {
      const { user: authUser, session } = await signInWithEmail(email, password);
      if (!authUser) {
        throw new Error('No se pudo iniciar sesión.');
      }

      const activeSession = session ?? (await getCurrentSession());
      if (!activeSession?.access_token || !activeSession?.refresh_token) {
        throw new Error('No se pudo recuperar la sesión del usuario.');
      }

      if (session?.access_token && session?.refresh_token) {
        await setSession(session.access_token, session.refresh_token);
      }

      const profile = await getUser(authUser.id);
      if (!profile) {
        const failureStatus = recordFailedLoginAttempt(trimmedEmail);
        void logAuthEvent({
          action: 'auth.login_failed',
          severity: 'warning',
          email: trimmedEmail,
          userId: authUser.id,
          details: {
            reason: 'profile_not_found',
            consecutiveFailures: failureStatus.consecutiveFailures,
          },
        });
        toast({
          title: 'Perfil incompleto',
          description: 'No se encontró el perfil del usuario.',
          variant: 'destructive',
          duration: 4000,
        });
        return false;
      }

      const normalizedEmail = profile.email && profile.email.length > 0
        ? profile.email
        : authUser.email ?? '';

      if (!ACCESS_ALLOWED_STATUSES.has(profile.status ?? 'inactive')) {
        await supabaseSignOut();
        const failureStatus = recordFailedLoginAttempt(trimmedEmail);
        void logAuthEvent({
          action: 'auth.login_failed',
          severity: 'critical',
          email: trimmedEmail,
          userId: profile.id,
          details: {
            reason: 'user_inactive',
            status: profile.status ?? 'inactive',
            consecutiveFailures: failureStatus.consecutiveFailures,
          },
        });
        const userStatus = profile.status ?? 'inactive';
        let statusTitle = 'Usuario inactivo';
        let statusDescription = 'Usuario inactivo, póngase en contacto con los administradores.';

        if (userStatus === 'without_account' || userStatus === 'sin_cuenta') {
          statusTitle = 'Cuenta de acceso cerrada';
          statusDescription = 'Esta cuenta se encuentra cerrada (propietario sin cuenta de acceso). Póngase en contacto con su administrador para solicitar una nueva invitación.';
        } else if (userStatus === 'blocked' || userStatus === 'bloqueada') {
          statusTitle = 'Cuenta bloqueada (Art. 32 LOPDGDD)';
          statusDescription = 'Esta cuenta se encuentra bloqueada legalmente. Póngase en contacto con la administración.';
        }

        toast({
          title: statusTitle,
          description: statusDescription,
          variant: 'destructive',
          duration: 5000,
        });
        return false;
      }

      const normalizedProfile = { ...profile, email: normalizedEmail };
      const enhancedProfile = await enhanceUserWithAvatar(normalizedProfile);
      if (enhancedProfile) {
        setCurrentUser(enhancedProfile);
        localStorage.setItem('currentUser', JSON.stringify(enhancedProfile));
        markUserHasLoggedIn();
      }

      resetFailedLoginAttempts(trimmedEmail);
      void logAuthEvent({
        action: 'auth.login',
        severity: 'info',
        email: trimmedEmail,
        userId: profile.id,
        communityId: profile.communityId ?? null,
        details: {
          role: profile.role,
        },
      });

      toast({
        title: 'Login exitoso',
        description: `Bienvenido/a, ${profile.name}`,
        duration: 3500,
      });
      return true;
    } catch (error: unknown) {
      const resolvedMessage = error instanceof Error ? error.message : null;
      const normalizedMessage = resolvedMessage?.trim().toLowerCase() ?? '';
      const failureStatus = recordFailedLoginAttempt(trimmedEmail);

      let reason = 'invalid_credentials';
      let isCritical = false;

      if (normalizedMessage.includes('email not confirmed')) {
        reason = 'email_not_confirmed';
      } else if (normalizedMessage.includes('user is banned') || normalizedMessage.includes('banned')) {
        reason = 'user_banned';
        isCritical = true;
      } else if (failureStatus.isRateLimited) {
        reason = 'rate_limit_exceeded';
        isCritical = true;
      }

      if (failureStatus.isRateLimited) {
        void logAuthEvent({
          action: 'auth.rate_limited',
          severity: 'critical',
          email: trimmedEmail,
          details: {
            reason: 'consecutive_login_failures_threshold_exceeded',
            consecutiveFailures: failureStatus.consecutiveFailures,
            retryAfterSeconds: failureStatus.retryAfterSeconds,
            scope: 'login',
          },
        });
      }

      void logAuthEvent({
        action: 'auth.login_failed',
        severity: isCritical ? 'critical' : 'warning',
        email: trimmedEmail,
        details: {
          reason,
          rawError: resolvedMessage,
          consecutiveFailures: failureStatus.consecutiveFailures,
          retryAfterSeconds: failureStatus.retryAfterSeconds,
        },
      });

      const errorMessage = failureStatus.isRateLimited
        ? `Has superado el límite de intentos. Espera ${failureStatus.retryAfterSeconds} segundos antes de volver a intentarlo.`
        : resolveLoginErrorMessage(resolvedMessage);

      toast({
        title: 'Error de acceso',
        description: errorMessage,
        variant: 'destructive',
        duration: 4000,
      });
      return false;
    }
  }, [enhanceUserWithAvatar, markUserHasLoggedIn, toast]);

  const refreshCurrentUser = React.useCallback(async (): Promise<User | null> => {
    const session = await getCurrentSession();
    if (!session?.user) {
      clearClientSession();
      return null;
    }

    const profile = await getUser(session.user.id);
    if (!profile) {
      clearClientSession();
      return null;
    }

    if (!ACCESS_ALLOWED_STATUSES.has(profile.status ?? 'inactive')) {
      clearClientSession();
      await supabaseSignOut();
      return null;
    }

    const normalizedEmail = profile.email && profile.email.length > 0
      ? profile.email
      : session.user.email ?? '';
    const enhanced = await enhanceUserWithAvatar({ ...profile, email: normalizedEmail });
    if (!enhanced) {
      clearClientSession();
      return null;
    }

    setCurrentUser(enhanced);
    localStorage.setItem('currentUser', JSON.stringify(enhanced));
    return enhanced;
  }, [clearClientSession, enhanceUserWithAvatar]);

  const refreshCurrentUserSilently = React.useCallback(async () => {
    if (profileRefreshInFlightRef.current) {
      return;
    }

    profileRefreshInFlightRef.current = true;
    try {
      const session = await getCurrentSession();
      if (!session?.user) {
        clearClientSession();
        return;
      }

      const profile = await getUser(session.user.id);
      if (!profile) {
        clearClientSession();
        return;
      }

      if (!ACCESS_ALLOWED_STATUSES.has(profile.status ?? 'inactive')) {
        clearClientSession();
        await supabaseSignOut();
        return;
      }

      const normalizedEmail = profile.email && profile.email.length > 0
        ? profile.email
        : session.user.email ?? '';

      if (
        currentUser?.id === profile.id &&
        currentUser.status === profile.status &&
        currentUser.email === normalizedEmail
      ) {
        return;
      }

      const enhanced = await enhanceUserWithAvatar({ ...profile, email: normalizedEmail });
      if (!enhanced) {
        clearClientSession();
        return;
      }

      setCurrentUser(enhanced);
      localStorage.setItem('currentUser', JSON.stringify(enhanced));
    } catch (error) {
      console.warn('No se pudo refrescar el perfil de la sesión activa:', error);
    } finally {
      profileRefreshInFlightRef.current = false;
    }
  }, [clearClientSession, currentUser?.email, currentUser?.id, currentUser?.status, enhanceUserWithAvatar]);

  const performLogout = React.useCallback(
    async (reason: 'manual' | 'inactivity' = 'manual') => {
      const exitingUserId = currentUser?.id ?? null;
      const exitingUserEmail = currentUser?.email ?? null;

      try {
        await supabaseSignOut();
        void logAuthEvent({
          action: 'auth.logout',
          severity: 'info',
          email: exitingUserEmail,
          userId: exitingUserId,
          details: { reason },
        });
        const toastContent =
          reason === 'inactivity'
            ? {
                title: 'Sesión caducada',
                description: 'Por seguridad, tu sesión se cerró tras un periodo de inactividad.',
                duration: 4000,
              }
            : {
                title: 'Sesión finalizada',
                description: 'Has cerrado sesión correctamente',
                duration: 3000,
              };
        toast(toastContent);
      } catch (error: unknown) {
        toast({
          title: 'Error al cerrar sesión',
          description: resolveErrorMessage(error, 'No se pudo cerrar la sesión. Inténtalo nuevamente.'),
          variant: 'destructive',
          duration: 4000,
        });
      } finally {
        clearClientSession();
      }
    },
    [clearClientSession, currentUser?.email, currentUser?.id, resolveErrorMessage, toast]
  );

  const logout = React.useCallback(async () => {
    await performLogout('manual');
  }, [performLogout]);

  const updateUserProfile = React.useCallback(async (userData: Partial<User>): Promise<boolean> => {
    if (!currentUser) return false;
    try {
      const { avatar, avatarPath, ...rest } = userData;
      const payload = omitUndefinedFields(rest);
      const hasPayload = Object.keys(payload).length > 0;
      const updated = hasPayload
        ? await updateUser(currentUser.id, payload)
        : ({ ...currentUser, ...payload } as User);
      const candidate: User = {
        ...updated,
        ...(avatar !== undefined ? { avatar } : {}),
        ...(avatarPath !== undefined ? { avatarPath } : {}),
      };
      const enhanced = await enhanceUserWithAvatar(candidate);
      if (enhanced) {
        setCurrentUser(enhanced);
        localStorage.setItem('currentUser', JSON.stringify(enhanced));
      }
      toast({
        title: "Perfil actualizado",
        description: "Los cambios han sido guardados correctamente",
        duration: 3000,
      });
      return true;
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: resolveErrorMessage(error, 'No se pudo actualizar el perfil'),
        variant: 'destructive',
        duration: 4000,
      });
      return false;
    }
  }, [currentUser, enhanceUserWithAvatar, resolveErrorMessage, toast]);

  const updatePassword = React.useCallback(async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!currentUser) return false;
    if (!currentUser.email) {
      toast({
        title: 'Error',
        description: 'No se pudo validar el correo electrónico del usuario.',
        variant: 'destructive',
        duration: 4000,
      });
      return false;
    }
    try {
      await signInWithEmail(currentUser.email, currentPassword);
      await updateAuthUser({ password: newPassword });
      void logAuthEvent({
        action: 'auth.password_updated',
        severity: 'info',
        email: currentUser.email,
        userId: currentUser.id,
        details: { scope: 'profile_settings' },
      });
      toast({
        title: 'Contraseña actualizada',
        description: 'La contraseña ha sido actualizada correctamente',
        duration: 3000,
      });
      return true;
    } catch (error: unknown) {
      const resolvedMessage = error instanceof Error ? error.message : null;
      const errorMessage = resolvedMessage === 'Invalid login credentials'
        ? 'La contraseña actual no es correcta.'
        : resolvedMessage ?? 'No se pudo actualizar la contraseña';
      void logAuthEvent({
        action: 'auth.password_update_failed',
        severity: 'warning',
        email: currentUser.email,
        userId: currentUser.id,
        details: {
          reason: resolvedMessage === 'Invalid login credentials' ? 'current_password_invalid' : 'password_update_error',
          rawError: resolvedMessage,
          scope: 'profile_settings',
        },
      });
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 4000,
      });
      return false;
    }
  }, [currentUser, toast]);
  
  const requestPasswordReset = React.useCallback(async (email: string): Promise<boolean> => {
    const trimmedEmail = email.trim().toLowerCase();
    try {
      await resetPasswordForEmail(trimmedEmail);
      void logAuthEvent({
        action: 'auth.password_reset_requested',
        severity: 'info',
        email: trimmedEmail,
      });
      toast({
        title: 'Correo enviado',
        description: 'Revisa tu bandeja de entrada para restablecer la contraseña.',
        duration: 4000,
      });
      return true;
    } catch (error: unknown) {
      void logAuthEvent({
        action: 'auth.password_reset_failed',
        severity: 'warning',
        email: trimmedEmail,
        details: {
          reason: 'request_reset_failed',
          rawError: resolveErrorMessage(error, 'unknown'),
        },
      });
      toast({
        title: 'Error',
        description: resolveErrorMessage(error, 'No se pudo enviar el correo de recuperación'),
        variant: 'destructive',
        duration: 4000,
      });
      return false;
    }
  }, [resolveErrorMessage, toast]);

  // Check for stored user on initial load
  React.useEffect(() => {
    const initialize = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        await syncUserFromSession(data.session);
      } finally {
        setIsLoading(false);
      }
    };

    void initialize();
  }, [syncUserFromSession]);

  React.useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_, session) => {
      void syncUserFromSession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [syncUserFromSession]);

  React.useEffect(() => {
    if (!currentUserId) {
      return undefined;
    }

    let inactivityTimeoutId: number | null = null;

    const clearInactivityTimer = () => {
      if (inactivityTimeoutId !== null) {
        window.clearTimeout(inactivityTimeoutId);
        inactivityTimeoutId = null;
      }
    };

    const scheduleInactivityLogout = () => {
      clearInactivityTimer();
      inactivityTimeoutId = window.setTimeout(() => {
        void performLogout('inactivity');
      }, GDPR_INACTIVITY_TIMEOUT_MS);
    };

    const activityHandler = () => {
      scheduleInactivityLogout();
    };

    const windowEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];

    const visibilityAwareHandler = () => {
      if (document.visibilityState === 'visible') {
        activityHandler();
      }
    };

    windowEvents.forEach((event) => {
      window.addEventListener(event, activityHandler, { passive: true });
    });
    document.addEventListener('visibilitychange', visibilityAwareHandler);

    scheduleInactivityLogout();

    return () => {
      clearInactivityTimer();
      windowEvents.forEach((event) => {
        window.removeEventListener(event, activityHandler);
      });
      document.removeEventListener('visibilitychange', visibilityAwareHandler);
    };
  }, [currentUserId, performLogout]);

  React.useEffect(() => {
    if (!currentUserId) {
      return undefined;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshCurrentUserSilently();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshCurrentUserSilently();
      }
    }, SESSION_PROFILE_REFRESH_INTERVAL_MS);

    void refreshCurrentUserSilently();

    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener('online', refreshWhenVisible);
    window.addEventListener('pageshow', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenVisible);
      window.removeEventListener('pageshow', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [currentUserId, refreshCurrentUserSilently]);

  const authContextValue = React.useMemo<AuthContextType>(() => ({
      currentUser,
      login,
      logout,
      isAuthenticated: !!currentUser,
      isLoading,
      updateUserProfile,
      updatePassword,
      requestPasswordReset,
      refreshCurrentUser,
    }), [
      currentUser,
      isLoading,
      login,
      logout,
      requestPasswordReset,
      refreshCurrentUser,
      updatePassword,
      updateUserProfile,
    ]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
